import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Check, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!token) setInvalid(true);
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => nav("/login"), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F5F7FA]">
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center mb-12">
            <img src="/promptyai-logo.png" alt="Promptyai.app" className="h-16 w-auto object-contain" />
          </Link>

          {invalid ? (
            <div data-testid="reset-invalid">
              <XCircle className="w-14 h-14 text-[#EF4444] mb-4" />
              <h1 className="font-display text-3xl font-bold mb-2">Invalid link.</h1>
              <p className="text-[#64748B] mb-6">This reset link is missing a token. Please request a new one.</p>
              <Link to="/forgot-password"><Button className="bg-[#1F3A8A] hover:bg-[#1E40AF] text-white">Request new link</Button></Link>
            </div>
          ) : done ? (
            <div data-testid="reset-done">
              <div className="w-14 h-14 rounded-full bg-[#14B8A6]/10 flex items-center justify-center mb-6">
                <Check className="w-7 h-7 text-[#14B8A6]" strokeWidth={2.5} />
              </div>
              <h1 className="font-display text-3xl font-bold mb-2">Password updated.</h1>
              <p className="text-[#64748B]">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">Choose a new password.</h1>
              <p className="text-[#64748B] mb-8">Use at least 8 characters. The reset link expires 30 minutes after you requested it.</p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <Label htmlFor="password">New password</Label>
                  <Input id="password" data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="confirm">Confirm password</Label>
                  <Input id="confirm" data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} className="mt-1.5" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#14B8A6] hover:bg-[#0D9488] text-white" data-testid="reset-submit">
                  {loading ? "Updating…" : "Update password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
      <div className="hidden lg:block relative bg-[#1F3A8A] bg-grid-dark overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1F3A8A] via-[#1F3A8A] to-[#14B8A6]/30"></div>
        <div className="relative h-full flex items-end p-12 text-white">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14B8A6] mb-3">Secure by default</div>
            <p className="font-display text-3xl font-bold leading-tight max-w-md">Your password is hashed with bcrypt and never stored in plain text.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
